import './_internal/CasTableToolbar.scss';

import { ReactNode, useState } from 'react';
import { Badge, Box } from '@mui/material';
import Iconify from 'src/components/iconify';
import useCasBoolean from '../../hooks/useCasBoolean';
import CasPopoverMenu, { IcasPopoverOption } from '../CasPopoverMenu/CasPopoverMenu';
import { ICasSelect } from '../CasSelect/CasSelect';
import { CasStackRow } from "../CasStack/CasStackRow";
import CasButton from '../CasButton/CasButton';
import CasRefreshButton from '../CasButton/CasRefreshButton';
import CasFilterChips from './_internal/CasFilterChips';
import CasFilterDrawer from './_internal/CasFilterDrawer';
import renderFilter from './_internal/renderFilter';
import { isFilterActive, emptyFilterValue } from './_internal/advancedFilters';

export type componentProps = Omit<ICasSelect, 'options'> & {
  includeAllOption?: boolean;
  includeNoneOption?: boolean;
  includeEmptyOption?: boolean;
  params?: Record<string, any>;
}

export interface ICasTableToolbar {
  onFilterChange: (name: string, value: any) => void;
  onApplyAdvanced?: (values: Record<string, any>) => void;
  /*
    Opt in to the filter drawer. When on, EVERY filter appears in the drawer;
    filters marked `isPrimaryFilter` additionally stay on the toolbar. Default
    (off) keeps the plain inline filter row.
  */
  filtersInDrawer?: boolean;
  filters?: any;
  /*
    Where the drawer portals to. Set by CasTable to its full-screen wrapper (see the
    comment there); undefined means MUI's default, <body>. Not a consumer prop.
  */
  drawerContainer?: HTMLElement | null;
  currentFilterValues: any;
  buttons: ReactNode[];
  refreshBtn?: boolean;
  refetch?: any;
  options?: IcasPopoverOption[];
}

const CasTableToolbar = ({
  onFilterChange,
  onApplyAdvanced,
  filtersInDrawer = false,
  filters,
  drawerContainer,
  currentFilterValues,
  buttons,
  refreshBtn = false,
  refetch = null,
  options,
}: ICasTableToolbar) => {
  if (refreshBtn && refetch == null)
    throw new Error(
      'CasTableToolbar: RefreshBtn cannot be true without specifying Refetch property'
    );

  const drawer = useCasBoolean();
  // Mount the drawer only once it has been opened, so its controls (and their option
  // fetches) don't build on every toolbar render. Stays mounted after that, so the
  // close transition still animates.
  const [drawerMounted, setDrawerMounted] = useState(false);

  const openDrawer = () => {
    setDrawerMounted(true);
    drawer.setTrue();
  };

  const allFilters: any[] = filters ?? [];
  // A batched-apply handler is required to commit the drawer's staged values;
  // without one (a bare CasTableToolbar consumer) fall back to the inline row.
  const useDrawer = filtersInDrawer && allFilters.length > 0 && !!onApplyAdvanced;

  /*
    Single pass. Drawer mode: the drawer gets every filter, `isPrimaryFilter`
    ones ALSO render inline (live), and chips cover only the applied filters
    that are not inline — a filter visible on the toolbar doesn't need a chip.
    Both copies of a primary share one value (keyed by `name`), so editing
    either one stays in sync.
  */
  const inlineFilters: any[] = [];
  const chipFilters: any[] = [];
  allFilters.forEach((filter: any) => {
    const inline = !useDrawer || filter.isPrimaryFilter;
    if (inline) inlineFilters.push(filter);
    else if (isFilterActive(currentFilterValues[filter.name])) chipFilters.push(filter);
  });

  const empty = !allFilters.length && !buttons?.length;

  return (
    <Box>
      <CasStackRow className={`CasTableToolbar ${empty ? 'empty' : ''}`} alignItems="center">
        <CasStackRow alignItems="center" flexGrow={1}>
          {refreshBtn && <CasRefreshButton refetch={refetch} />}
          {inlineFilters.map((f) =>
            renderFilter(f, currentFilterValues[f.name], (v) => onFilterChange(f.name, v))
          )}
          {useDrawer && (
            <Badge badgeContent={chipFilters.length} color="primary">
              <CasButton
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Iconify icon="mdi:filter-variant" width={18} />}
                onClick={openDrawer}
              >
                {inlineFilters.length > 0 ? 'More' : 'Filters'}
              </CasButton>
            </Badge>
          )}
        </CasStackRow>
        <CasStackRow alignItems="center" justifyContent="flex-end">
          {buttons}
        </CasStackRow>
        {options && options?.length > 0 && <CasPopoverMenu options={options} />}
      </CasStackRow>

      {useDrawer && (
        <>
          <CasFilterChips
            filters={chipFilters}
            values={currentFilterValues}
            onOpen={openDrawer}
            onClear={(name) => onFilterChange(name, emptyFilterValue(currentFilterValues[name]))}
          />
          {drawerMounted && (
            <CasFilterDrawer
              open={drawer.value}
              onClose={drawer.setFalse}
              filters={allFilters}
              appliedValues={currentFilterValues}
              onApply={onApplyAdvanced}
              container={drawerContainer}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CasTableToolbar;
